import RequestProgressService from 'requestProgress/RequestProgressService';
import { UserPreferencesApi } from './UserPreferencesApi';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { Store } from 'redux';
import { FetchedResultCache } from 'utils/FetchedResultCache';
import {
    savedPreference,
    setPreference,
} from 'userPreferences/userPreferencesSlice';

export interface UserPreferencesServiceOptions {
    api: UserPreferencesApi;
    requestProgressService: RequestProgressService;
    loggingService: LoggingService;
}

type GetUserPrefOptions<T = void> = { key: string; initialValue?: T };

const toUid = (options: GetUserPrefOptions<unknown>) => options.key;

export class UserPreferencesService {
    private api: UserPreferencesApi;
    private requestProgressService: RequestProgressService;
    private logger: Logger;

    private cache: FetchedResultCache<unknown, GetUserPrefOptions<unknown>>;

    public store: Store | undefined;

    public constructor(options: UserPreferencesServiceOptions) {
        this.api = options.api;
        this.requestProgressService = options.requestProgressService;
        this.logger = buildLogger(
            'UserPreferencesService',
            options.loggingService
        );

        this.cache = new FetchedResultCache<
            unknown,
            GetUserPrefOptions<unknown>
        >({
            toUid,
            fetchResult: this.fetchResult.bind(this),
            onNewResultFetched: this.onNewResultFetched.bind(this),
            timeoutMins: 5,
            logger: this.logger,
        });
    }

    public async fetchUserPreference<T>(
        key: string,
        initialValue?: T
    ): Promise<T | undefined> {
        const options: GetUserPrefOptions<T> = { key, initialValue };
        const result = await this.cache.fetch(options);
        return result as T;
    }

    public async saveUserPreference<T>(key: string, body: T): Promise<void> {
        this.logger.debug(`Setting "${key}"`);

        this.cache.set({ key }, body);

        this.store!.dispatch(setPreference({ key, setting: body }));

        await this.requestProgressService.invokeAndMonitorRequest(
            'saveUserPreference',
            (options) => this.api.saveUserPreference(key, body, options)
        );

        this.store!.dispatch(savedPreference({ key, setting: body }));
    }

    private fetchResult(options: GetUserPrefOptions<unknown>) {
        return this.requestProgressService.invokeAndMonitorRequest(
            'fetchUserPreference',
            (apiOptions) =>
                this.api.fetchUserPreference(options.key, apiOptions)
        );
    }

    private onNewResultFetched(
        options: GetUserPrefOptions<unknown>,
        result: unknown
    ) {
        const setting = result ?? options.initialValue;
        this.store!.dispatch(setPreference({ key: options.key, setting }));
    }
}
